import { useState } from 'react';
import { Box, InlineStack, Search, SegmentedControl } from '@customerio/pluma-components/react';
import type { CustomerSearchQuery } from '@/services/db/customer/merge';
import { isEmptyString } from '@/utils/string';
import { EmailSearch } from '../../components/email-search';

/** How a person is looked up: by exact email, or by any identifier value. */
export type SearchBy = 'email' | 'id';

const SEARCH_BY_OPTIONS = [
  { value: 'email', label: 'email address' },
  { value: 'id', label: 'id' },
];

type CustomerSearchProps = {
  /** Distinguishes the two cards' mode controls. */
  name: string;
  searchBy: SearchBy;
  onSearchByChange: (searchBy: SearchBy) => void;
  onSearch: (query: CustomerSearchQuery) => void;
  onClearSearch: () => void;
};

/**
 * The mode control plus the input for the active mode. Switching mode clears
 * the previous results; a blank submission clears them too.
 */
export function CustomerSearch({ name, searchBy, onSearchByChange, onSearch, onClearSearch }: CustomerSearchProps) {
  const [id, setId] = useState('');

  const searchByEmail = (term: string) => {
    if (isEmptyString(term)) {
      onClearSearch();
    } else {
      onSearch({ email: term });
    }
  };

  const searchById = (value: string) => {
    if (!isEmptyString(value)) {
      onSearch({ id: value });
    }
  };

  return (
    <Box data-test="people-merge-customer-search">
      <InlineStack alignBlock="center" alignInline="space-between">
        <SegmentedControl
          name={name}
          ariaLabel="Search by"
          variant="primary"
          size="sm"
          value={searchBy}
          options={SEARCH_BY_OPTIONS}
          onChange={(value: string) => {
            onSearchByChange(value === 'id' ? 'id' : 'email');
            onClearSearch();
          }}
        />
      </InlineStack>
      <Box mt="075" mb="100">
        {searchBy === 'email' ? (
          <EmailSearch term="" onSearch={searchByEmail} />
        ) : (
          <Search
            ariaLabel="id"
            placeholder="Find by id…"
            value={id}
            onSearchInput={setId}
            onSearch={searchById}
            onClear={() => {
              setId('');
              onClearSearch();
            }}
            shouldSearchOnEnter
            data-test="id-search"
          />
        )}
      </Box>
    </Box>
  );
}
